/**
 * Модалка «Добавить площадку» / «Изменить площадку» раздела «Экология» (ecology-sites, макет
 * варианта Б 17.09.2026). Площадка — производственный объект с адресом; код объекта НВОС и
 * категория — из свидетельства о постановке на учёт. Название и адрес обязательны. Категория: I–IV, «Без категории» (не объект
 * НВОС) или «Не знаю» — тогда подсказка пройти опрос по площадке (опрос — следующий этап; категорию
 * по нему поставит эколог Центрики). Сервер проверяет поля авторитетно и отвечает свежим обзором.
 */
import { useEffect, useId, useState, type FormEvent } from 'react'
import type { EcoOverview, EcoScopeApi, EcoSite, EcoSiteInput } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { ECO_SITE_CATEGORY_OPTIONS } from '@/lib/ecology'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'

/** Выделение выбранного варианта — как у выбора вариантов в формах проекта. */
const SELECTED_OPTION = 'border-accent bg-accent-bg text-accent-txt hover:bg-accent-bg'

const EMPTY: EcoSiteInput = { name: '', address: '', nvosCode: '', category: '' }

function inputFrom(site: EcoSite | null): EcoSiteInput {
  return site
    ? { name: site.name, address: site.address, nvosCode: site.nvosCode, category: site.category }
    : EMPTY
}

export function SiteDialog({
  open,
  site,
  api,
  onClose,
  onSaved,
}: {
  open: boolean
  /** Правка площадки; null — новая. */
  site: EcoSite | null
  api: EcoScopeApi
  onClose: () => void
  onSaved: (overview: EcoOverview) => void
}) {
  const formId = useId()
  const [input, setInput] = useState<EcoSiteInput>(() => inputFrom(site))
  const [showErrors, setShowErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setInput(inputFrom(site))
    setShowErrors(false)
    setError(null)
  }, [open, site])

  const nameError = showErrors && !input.name.trim() ? 'Укажите название площадки' : undefined
  const addressError = showErrors && !input.address.trim() ? 'Укажите адрес площадки' : undefined
  const title = site ? 'Изменить площадку' : 'Добавить площадку'

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    if (!input.name.trim() || !input.address.trim()) {
      setShowErrors(true)
      return
    }
    setError(null)
    setSaving(true)
    try {
      const body: EcoSiteInput = {
        name: input.name.trim(),
        address: input.address.trim(),
        nvosCode: input.nvosCode.trim(),
        category: input.category,
      }
      const overview = site ? await api.updateSite(site.id, body) : await api.createSite(body)
      onSaved(overview)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить площадку'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) onClose()
      }}
    >
      <DialogContent title={title} className="max-w-xl">
        <Card
          scrollable
          className="max-h-dialog"
          header={title}
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Отмена
              </Button>
              <Button type="submit" form={formId} disabled={saving}>
                {saving ? 'Сохранение…' : site ? 'Сохранить' : 'Добавить площадку'}
              </Button>
            </div>
          }
        >
          <form id={formId} onSubmit={submit} noValidate>
            <p className="text-13 text-txt-secondary">
              Площадка — производственный объект с адресом. Если объект поставлен на учёт, укажите
              код и категорию из свидетельства.
            </p>
            {error ? (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            ) : null}
            <Field label="Название" required error={nameError}>
              {(props) => (
                <Input
                  {...props}
                  value={input.name}
                  placeholder="Например: Склад готовой продукции"
                  invalid={Boolean(nameError)}
                  onChange={(e) => setInput({ ...input, name: e.target.value })}
                />
              )}
            </Field>
            <Field label="Адрес" required error={addressError}>
              {(props) => (
                <Input
                  {...props}
                  value={input.address}
                  placeholder="Регион, населённый пункт, улица"
                  invalid={Boolean(addressError)}
                  onChange={(e) => setInput({ ...input, address: e.target.value })}
                />
              )}
            </Field>
            <Field label="Код объекта НВОС" hint="Если есть свидетельство о постановке на учёт">
              {(props) => (
                <Input
                  {...props}
                  className="max-w-xs"
                  value={input.nvosCode}
                  placeholder="00-0000-000000-П"
                  onChange={(e) => setInput({ ...input, nvosCode: e.target.value })}
                />
              )}
            </Field>
            <div className="my-4">
              <p className="mb-1.5 text-13 text-txt-secondary">Категория объекта НВОС</p>
              <div
                className="flex flex-wrap gap-2"
                role="group"
                aria-label="Категория объекта НВОС"
              >
                {ECO_SITE_CATEGORY_OPTIONS.map((o) => {
                  const on = input.category === o.value
                  return (
                    <Button
                      key={o.value || 'unknown'}
                      type="button"
                      size="sm"
                      variant="secondary"
                      aria-pressed={on}
                      className={cn('min-w-12', on && SELECTED_OPTION)}
                      onClick={() => setInput({ ...input, category: o.value })}
                    >
                      {o.label}
                    </Button>
                  )
                })}
              </div>
              <p className="mt-2 text-12 text-txt-tertiary">
                {input.category === ''
                  ? 'Не знаете категорию — пройдите опрос по площадке: по ответам категорию проставит эколог Центрики.'
                  : input.category === 'none'
                    ? 'Площадка не стоит на учёте как объект негативного воздействия — у неё свой набор документов.'
                    : 'Категория I–IV — «экологический класс» площадки: чем она выше, тем больше документов и отчётности.'}
              </p>
            </div>
          </form>
        </Card>
      </DialogContent>
    </Dialog>
  )
}
